import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import store from '../store'
import LoginView from '@/views/LoginView.vue';
import ChangePasswordView from '@/views/ChangePasswordView.vue';


import DashboardView from '@/views/DashboardView.vue';
import UsersView from '@/views/users/UsersView.vue';
import UserShowPage from '@/views/users/UserShowPage.vue';
import ActivityLogsView from '@/views/users/ActivityLogsView.vue';
import ProductsView from '@/views/products/ProductsView.vue';
import ProductDatabaseView from '@/views/products/ProductDatabaseView.vue';
import ProductShowView from '@/views/products/ProductShowView.vue';
import ProductAssignmentView from '@/views/products/ProductAssignmentView.vue';
import PromotionsView from '@/views/products/PromotionsView.vue';
import StockMovementsView from '@/views/products/StockMovementsView.vue';
import StockCalendarView from '@/views/products/StockCalendarView.vue';
import StockTransfersView from '@/views/products/StockTransfersView.vue';
import WalletsView from '@/views/wallets/WalletsView.vue';
import StockTransferCreateView from '@/views/products/StockTransferCreateView.vue';
import StockTransferEditView from '@/views/products/StockTransferEditView.vue';
import StockTransferShowView from '@/views/products/StockTransferShowView.vue';
import SalesView from '@/views/sales/SalesView.vue';
import SaleShowView from '@/views/sales/SaleShowView.vue';
import InventoriesView from '@/views/inventories/InventoriesView.vue';
import InventoryShowView from '@/views/inventories/InventoryShowView.vue';
import CashierInventoriesView from '@/views/inventories/CashierInventoriesView.vue';
import InventoryProcessView from '@/views/inventories/InventoryProcessView.vue';
import PurchasesView from '@/views/purchases/PurchasesView.vue';
import PurchaseShowView from '@/views/purchases/PurchaseShowView.vue';
import PurchaseEditView from '@/views/purchases/PurchaseEditView.vue';
import SupplierReturnsView from '@/views/purchases/SupplierReturnsView.vue';
import SupplierReturnCreateView from '@/views/purchases/SupplierReturnCreateView.vue';
import SupplierReturnEditView from '@/views/purchases/SupplierReturnEditView.vue';
import SupplierReturnShowView from '@/views/purchases/SupplierReturnShowView.vue';
import ClientsView from '@/views/clients/ClientsView.vue';
import ClientShowView from '@/views/clients/ClientShowView.vue';
import SuppliersView from '@/views/suppliers/SuppliersView.vue';
import SupplierShowView from '@/views/suppliers/SupplierShowView.vue';
import RaysView from '@/views/rayons/RaysView.vue';
import RayShowView from '@/views/rayons/RayShowView.vue';
import CategoriesView from '@/views/categories/CategoriesView.vue';
import CategoryShowView from '@/views/categories/CategoryShowView.vue';
import CashCountsView from '@/views/cash/CashCountsView.vue';
import CashCountShowView from '@/views/cash/CashCountShowView.vue';
import CashRegistersView from '@/views/cash/CashRegistersView.vue';
import CashRegisterShowView from '@/views/cash/CashRegisterShowView.vue';
import AdminDaySummaryView from '@/views/cash/AdminDaySummaryView.vue';
import DeliveryShowView from '@/views/deliveries/DeliveryShowView.vue';
import ShopInfoView from '@/views/ShopInfoView.vue';
import SitesView from '@/views/sites/SitesView.vue';
import SiteShowView from '@/views/sites/SiteShowView.vue';
import SalesReportView from '@/views/reports/SalesReportView.vue';
import RevenueMarginsReportView from '@/views/reports/RevenueMarginsReportView.vue';
import PurchasesReportView from '@/views/reports/PurchasesReportView.vue';
import InventoriesReportView from '@/views/reports/InventoriesReportView.vue';
import ProductsReportView from '@/views/reports/ProductsReportView.vue';
import ModuleReportView from '@/views/reports/ModuleReportView.vue';
import CashierEndOfDayView from '@/views/cash/CashierEndOfDayView.vue';
import AdminManualView from '@/views/manuals/AdminManualView.vue';
import CashierManualView from '@/views/manuals/CashierManualView.vue';
import { hasAllPermissions, hasAnyPermission, hasPermission, routePermissions } from '@/support/permissions';
import { startRouteLoading, stopRouteLoading } from '@/support/navigationLoading';
import { ensureShopInfoForNavigation, isCurrentShopKnownIncomplete } from '@/support/shopNavigation';
import { appScrollBehavior } from '@/support/routerScroll';
import { authenticatedLandingRoute } from '@/support/authenticatedLanding';
import { applyDefaultAppSeo } from '@/support/publicSeo';

const BACKOFFICE_PREFIX = '/backoffice';

const backofficePath = (path: string): string => `${BACKOFFICE_PREFIX}${path}`;

const routes: Array<RouteRecordRaw> = [
  // Les anciennes URL publiques restent reconnues et ouvrent désormais la gestion.
  ...[
    { path: '/', name: 'PublicHome' },
    { path: '/produits/:slug', name: 'PublicProductShow' },
    { path: '/commande', name: 'PublicCheckout' },
    { path: '/boutiques', name: 'PublicShops' },
    { path: '/contact', name: 'PublicContact' },
    { path: '/connexion', name: 'PublicLogin' },
    { path: '/inscription', name: 'PublicRegister' },
    { path: '/verification-email', name: 'PublicVerifyEmail' },
    { path: '/mot-de-passe-oublie', name: 'PublicForgotPassword' },
    { path: '/reinitialiser-mot-de-passe', name: 'PublicResetPassword' },
    { path: '/boutique-indisponible', name: 'PublicUnavailable' },
  ].map(({ path, name }) => ({
    path,
    name,
    // Ne pas transmettre au backoffice les paramètres ni les secrets des liens clients.
    redirect: { name: 'Login', params: {}, query: {}, hash: '' },
  })),
  {
    path: BACKOFFICE_PREFIX,
    redirect: { name: 'Sites' },
  },
  {
    path: backofficePath('/login'),
    name: 'Login',
    component: LoginView,
    meta: { redirectIfAuth: true, requiresShopSelected: false },
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('@/views/ResetPasswordView.vue'),
    meta: { requiresShopSelected: false },
  },
  {
    path: backofficePath('/change-password'),
    name: 'ChangePassword',
    component: ChangePasswordView,
    meta: { requiresAuth: true, requiresShopSelected: false },
  },
  {
    path: backofficePath('/manuel-admin'),
    name: 'AdminManual',
    component: AdminManualView,
    meta: { requiresAuth: true, allowedRoles: ['gerant', 'utilisateur'], requiresShopSelected: false },
  },
  {
    path: backofficePath('/cashier/manuel'),
    name: 'CashierManual',
    component: CashierManualView,
    meta: { requiresAuth: true, allowedRoles: ['gerant', 'caissier'], requiresShopSelected: false },
  },
  {
    path: backofficePath('/mes-sites'),
    name: 'Sites',
    component: SitesView,
    meta: {
      requiresAuth: true,
      allowedRoles: ['gerant', 'utilisateur'], requiresShopSelected: false
    },
  },
  {
    path: backofficePath('/mes-sites/base-de-donnee'),
    name: 'AllSitesProductDatabase',
    component: ProductDatabaseView,
    props: { allSitesMode: true },
    meta: { requiresAuth: true, allowedRoles: ['gerant', 'utilisateur'], permission: 'products.view', requiresShopSelected: false },
  },
  {
    path: backofficePath('/mes-sites/utilisateurs'),
    name: 'AllSitesUsers',
    component: UsersView,
    props: { allSitesMode: true },
    meta: { requiresAuth: true, allowedRoles: ['gerant', 'support', 'superadmin', 'super_admin'], permission: 'users.view', requiresShopSelected: false },
  },
  {
    path: backofficePath('/mes-sites/utilisateurs/:id'),
    name: 'AllSitesUserShow',
    component: UsersView,
    props: { allSitesMode: true },
    meta: { requiresAuth: true, allowedRoles: ['gerant', 'support', 'superadmin', 'super_admin'], permission: 'users.view', requiresShopSelected: false },
  },
  {
    path: backofficePath('/parametres/sites/:id'),
    name: 'SiteShow',
    component: SiteShowView,
    meta: {
      requiresAuth: true,
      allowedRoles: ['gerant'], requiresShopSelected: false
    },
  },
  {
    path: backofficePath('/parametres'),
    redirect: { name: 'SettingsGeneral' },
  },
  {
    path: backofficePath('/parametres/general'),
    name: 'SettingsGeneral',
    component: () => import('@/views/settings/SettingsGeneralView.vue'),
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/parametres/impression'),
    name: 'SettingsPrinting',
    component: () => import('@/views/settings/SettingsPrintingView.vue'),
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/parametres/inventaire'),
    name: 'SettingsInventory',
    component: () => import('@/views/settings/SettingsInventoryView.vue'),
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/parametres/comptabilite'),
    name: 'SettingsAccounting',
    component: () => import('@/views/settings/SettingsAccountingView.vue'),
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/parametres/ecommerce'),
    name: 'SettingsEcommerce',
    component: () => import('@/views/settings/SettingsEcommerceView.vue'),
    meta: {
      requiresAuth: true,
      allowedRoles: ['gerant'],
      requiresShopSelected: true,
      requiresEcommerce: true,
      requiresPublishedMainEcommerce: true,
    },
  },
  {
    path: backofficePath('/livraisons'),
    name: 'DeliveriesView',
    component: () => import('@/views/deliveries/DeliveriesView.vue'),
    meta: { requiresAuth: true, allowedRoles: ['gerant', 'superadmin'], requiresShopSelected: false, requiresEcommerce: true },
  },
  {
    path: backofficePath('/livraisons/:id'),
    name: 'DeliveryShow',
    component: DeliveryShowView,
    meta: { requiresAuth: true, allowedRoles: ['gerant', 'superadmin'], requiresShopSelected: false, requiresEcommerce: true },
  },
  {
    path: backofficePath('/parametres/livraison'),
    name: 'DeliverySettings',
    component: () => import('@/views/DeliverySettings.vue'),
    meta: { requiresAuth: true, allowedRoles: ['gerant', 'superadmin'], requiresEcommerce: true },
  },
  {
    path: backofficePath('/parametres/mouvements'),
    name: 'MovementSettings',
    component: () => import('@/views/MovementSettingsView.vue'),
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/parametres/types-depenses'),
    name: 'ExpenseTypes',
    component: () => import('@/views/expenses/ExpenseTypesView.vue'),
    meta: { requiresAuth: true, allowedRoles: ['gerant', 'utilisateur', 'support', 'superadmin', 'super_admin'], requiresShopSelected: true, permission: 'expenses.manage_types' },
  },
  {
    path: backofficePath('/tableau-de-bord'),
    name: 'Dashboard',
    component: DashboardView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/parametres/boutique'),
    name: 'ShopInfo',
    component: ShopInfoView,
    meta: {
      requiresAuth: true,
      allowedRoles: ['gerant'],
      requiresShopSelected: true,
      // requiresShopInfo: false,   
    },
  },
  {
    path: backofficePath('/utilisateurs'),
    name: 'Users',
    component: UsersView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/utilisateurs/activites-recentes'),
    name: 'ActivityLogs',
    component: ActivityLogsView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/utilisateurs/:id'),
    name: 'UserShow',
    component: UserShowPage,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/base-de-donnee-produits'),
    name: 'ProductDatabase',
    component: ProductDatabaseView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/produits'),
    name: 'Products',
    component: ProductsView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/promotions'),
    name: 'ProductPromotions',
    component: PromotionsView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true, permission: 'promotions.view' },
  },
  {
    path: backofficePath('/produits/:id'),
    name: 'ProductShow',
    component: ProductShowView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/calendrier-stock'),
    name: 'StockCalendar',
    component: StockCalendarView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true, permission: 'stock_movements.view' },
  },
  {
    path: backofficePath('/mouvements-stock'),
    name: 'StockMovements',
    component: StockMovementsView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/portefeuilles-electroniques'),
    name: 'ElectronicWallets',
    component: WalletsView,
    meta: { requiresAuth: true, allowedRoles: ['gerant', 'utilisateur', 'support', 'superadmin', 'super_admin'], requiresShopSelected: true, permission: 'wallet.view' },
  },
  {
    path: backofficePath('/transferts-stock'),
    name: 'StockTransfers',
    component: StockTransfersView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/transferts-stock/nouveau'),
    name: 'StockTransferCreate',
    component: StockTransferCreateView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true, permission: 'stock_transfers.create' },
  },
  {
    path: backofficePath('/transferts-stock/:id/modifier'),
    name: 'StockTransferEdit',
    component: StockTransferEditView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true, permission: 'stock_transfers.update' },
  },
  {
    path: backofficePath('/transferts-stock/:id'),
    name: 'StockTransferShow',
    component: StockTransferShowView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true, permission: 'stock_transfers.view' },
  },
  {
    path: backofficePath('/ventes'),
    name: 'Sales',
    component: SalesView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/ventes/devis'),
    name: 'SalesQuotes',
    component: SalesView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true, permission: 'sales.view' },
  },
  {
    path: backofficePath('/ventes/journees'),
    name: 'SalesDaySummary',
    component: AdminDaySummaryView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true, permission: 'cash_counts.view' },
  },
  {
    path: backofficePath('/ventes/:id'),
    name: 'SaleShow',
    component: SaleShowView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/cashier/ventes'),
    name: 'CashierSales',
    component: () => import('@/views/sales/CashierSalesView.vue'),
    meta: { requiresAuth: true, allowedRoles: ['gerant', 'caissier'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/cashier/afficheur-client'),
    name: 'CashierCustomerDisplay',
    component: () => import('@/views/sales/CustomerDisplayView.vue'),
    meta: { requiresAuth: true, allowedRoles: ['gerant', 'caissier'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/cashier/fin-journee'),
    name: 'CashierEndOfDay',
    component: CashierEndOfDayView,
    meta: { requiresAuth: true, allowedRoles: ['gerant', 'caissier'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/inventaires'),
    name: 'Inventories',
    component: InventoriesView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/inventaires/:id/comptage'),
    name: 'InventoryCount',
    component: InventoryProcessView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true, permission: 'inventories.count' },
  },
  {
    path: backofficePath('/inventaires/:id'),
    name: 'InventoryShow',
    component: InventoryShowView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/cashier/inventaires'),
    name: 'CashierInventories',
    component: CashierInventoriesView,
    meta: { requiresAuth: true, allowedRoles: ['gerant', 'caissier'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/cashier/inventaires/:id/comptage'),
    name: 'CashierInventoryCount',
    component: InventoryProcessView,
    meta: { requiresAuth: true, allowedRoles: ['gerant', 'caissier'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/achats'),
    name: 'Purchases',
    component: PurchasesView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/achats/:id/modifier'),
    name: 'PurchaseEdit',
    component: PurchaseEditView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/achats/:id'),
    name: 'PurchaseShow',
    component: PurchaseShowView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/depenses'),
    name: 'Expenses',
    component: () => import('@/views/expenses/ExpensesView.vue'),
    meta: { requiresAuth: true, allowedRoles: ['gerant', 'utilisateur', 'caissier', 'support', 'superadmin', 'super_admin'], requiresShopSelected: true, permissionsAny: ['expenses.view', 'expenses.create', 'expenses.manage_recurring'] },
  },
  {
    path: backofficePath('/depenses/recurrences'),
    name: 'RecurringExpenses',
    redirect: { name: 'Expenses', query: { planifications: '1' } },
    meta: { requiresAuth: true, allowedRoles: ['gerant', 'utilisateur', 'support', 'superadmin', 'super_admin'], requiresShopSelected: true, permission: 'expenses.manage_recurring' },
  },
  {
    path: backofficePath('/depenses/:id(\\d+)/modifier'),
    name: 'ExpenseEdit',
    redirect: (to) => ({ name: 'ExpenseShow', params: { id: to.params.id }, query: { ...to.query, edit: '1' } }),
    meta: { requiresAuth: true, allowedRoles: ['gerant', 'utilisateur', 'support', 'superadmin', 'super_admin'], requiresShopSelected: true, permission: 'expenses.update' },
  },
  {
    path: backofficePath('/depenses/:id(\\d+)'),
    name: 'ExpenseShow',
    component: () => import('@/views/expenses/ExpenseShowView.vue'),
    meta: { requiresAuth: true, allowedRoles: ['gerant', 'utilisateur', 'caissier', 'support', 'superadmin', 'super_admin'], requiresShopSelected: true, permission: 'expenses.view' },
  },
  {
    path: backofficePath('/retours-fournisseurs'),
    name: 'SupplierReturns',
    component: SupplierReturnsView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/retours-fournisseurs/nouveau'),
    name: 'SupplierReturnCreate',
    component: SupplierReturnCreateView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/retours-fournisseurs/:id/modifier'),
    name: 'SupplierReturnEdit',
    component: SupplierReturnEditView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/retours-fournisseurs/:id'),
    name: 'SupplierReturnShow',
    component: SupplierReturnShowView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/clients'),
    name: 'Clients',
    component: ClientsView,
    meta: { requiresAuth: true, allowedRoles: ['gerant', 'caissier'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/clients/:id'),
    name: 'ClientShow',
    component: ClientShowView,
    meta: { requiresAuth: true, allowedRoles: ['gerant', 'caissier'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/fournisseurs'),
    name: 'Suppliers',
    component: SuppliersView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/fournisseurs/:id'),
    name: 'SupplierShow',
    component: SupplierShowView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/rayons'),
    name: 'Rays',
    component: RaysView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/rayons/:id/associer-produits'),
    name: 'RayProductAssignment',
    component: ProductAssignmentView,
    props: { targetType: 'rayon' },
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/rayons/:id'),
    name: 'RayShow',
    component: RayShowView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/categories'),
    name: 'Categories',
    component: CategoriesView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/categories/:id/associer-produits'),
    name: 'CategoryProductAssignment',
    component: ProductAssignmentView,
    props: { targetType: 'category' },
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/categories/:id'),
    name: 'CategoryShow',
    component: CategoryShowView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },

  {
    path: backofficePath('/cash-counts'),
    name: 'CashCounts',
    component: CashCountsView,
    meta: { requiresAuth: true, allowedRoles: ['gerant', 'caissier'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/caisses'),
    name: 'CashRegisters',
    component: CashRegistersView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/caisses/:id'),
    name: 'CashRegisterShow',
    component: CashRegisterShowView,
    meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
  },
  {
    path: backofficePath('/cash-counts/:id'),
    name: 'CashCountShow',
    component: CashCountShowView,
    meta: { requiresAuth: true, allowedRoles: ['gerant', 'caissier'], requiresShopSelected: true },
  },
  {
  path: backofficePath('/rapports/ventes'),
  name: 'SalesReport',
  component: SalesReportView,
  meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
},
{
  path: backofficePath('/rapports/depenses'),
  name: 'ExpensesReport',
  component: () => import('@/views/expenses/ExpensesReportView.vue'),
  meta: {
    requiresAuth: true,
    allowedRoles: ['gerant', 'utilisateur', 'support', 'superadmin', 'super_admin'],
    requiresShopSelected: true,
    permission: 'reports.view',
    permissionsAll: ['reports.view', 'expenses.view'],
  },
},
{
  path: backofficePath('/rapports/chiffre-affaires-marges'),
  name: 'RevenueMarginsReport',
  component: RevenueMarginsReportView,
  meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
},
{
  path: backofficePath('/rapports/achats'),
  name: 'PurchasesReport',
  component: PurchasesReportView,
  meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
},
{
  path: backofficePath('/rapports/inventaires'),
  name: 'InventoriesReport',
  component: InventoriesReportView,
  meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
},
{
  path: backofficePath('/rapports/produits'),
  name: 'ProductsReport',
  component: ProductsReportView,
  meta: { requiresAuth: true, allowedRoles: ['gerant'], requiresShopSelected: true },
},
{
  path: backofficePath('/rapports/retours-fournisseurs'),
  name: 'SupplierReturnsReport',
  component: ModuleReportView,
  meta: {
    requiresAuth: true,
    allowedRoles: ['gerant'],
    requiresShopSelected: true,
    report: {
      title: 'Rapport des retours fournisseur',
      subtitle: 'Montants retournés, fournisseurs concernés et statut de validation.',
      endpoint: '/reports/supplier-returns',
      fileName: 'rapport_retours_fournisseurs.xlsx',
      sheetName: 'Retours',
      statusOptions: [
        { value: 'draft', label: 'Brouillon' },
        { value: 'validated', label: 'Validé' },
      ],
      summaryCards: [
        { label: 'Retours', key: 'total', format: 'number' },
        { label: 'Montant retourné', key: 'sum.total_amount', format: 'currency' },
        { label: 'Lignes produits', key: 'sum.items_count', format: 'number' },
      ],
      columns: [
        { key: 'reference', label: 'Référence', sortable: true },
        { key: 'status', label: 'Statut', sortable: true, class: 'text-center' },
        { key: 'supplier_name', label: 'Fournisseur', sortable: true },
        { key: 'purchase_reference', label: 'Commande', sortable: true },
        { key: 'return_date', label: 'Date retour', sortable: true },
        { key: 'items_count', label: 'Lignes', sortable: true, class: 'text-end' },
        { key: 'total_amount', label: 'Montant', sortable: true, class: 'text-end' },
        { key: 'validated_at', label: 'Validation', sortable: true },
      ],
    },
  },
},
  {
    path: backofficePath('/rapports/portefeuilles'),
    name: 'WalletsReport',
    component: WalletsView,
    meta: { requiresAuth: true, allowedRoles: ['gerant', 'utilisateur', 'support', 'superadmin', 'super_admin'], requiresShopSelected: true, permission: 'wallet.report' },
  },
{
  path: backofficePath('/rapports/paiements-fournisseurs'),
  name: 'SupplierPaymentsReport',
  component: ModuleReportView,
  meta: {
    requiresAuth: true,
    allowedRoles: ['gerant'],
    requiresShopSelected: true,
    report: {
      title: 'Rapport des paiements fournisseur',
      subtitle: 'Règlements enregistrés par commande validée et reste dû associé.',
      endpoint: '/reports/supplier-payments',
      fileName: 'rapport_paiements_fournisseurs.xlsx',
      sheetName: 'Paiements',
      statusOptions: [],
      summaryCards: [
        { label: 'Paiements', key: 'total', format: 'number' },
        { label: 'Montant payé', key: 'sum.amount', format: 'currency' },
      ],
      columns: [
        { key: 'payment_date', label: 'Date paiement', sortable: true },
        { key: 'supplier_name', label: 'Fournisseur', sortable: true },
        { key: 'purchase_reference', label: 'Commande', sortable: true },
        { key: 'amount', label: 'Montant', sortable: true, class: 'text-end' },
        { key: 'method', label: 'Méthode', sortable: true },
        { key: 'reference', label: 'Référence', sortable: true },
        { key: 'user_name', label: 'Utilisateur', sortable: true },
      ],
    },
  },
},
{
  path: backofficePath('/rapports/mouvements-stock'),
  name: 'StockMovementsReport',
  component: ModuleReportView,
  meta: {
    requiresAuth: true,
    allowedRoles: ['gerant'],
    requiresShopSelected: true,
    report: {
      title: 'Rapport des mouvements de stock',
      subtitle: 'Entrées, sorties et motifs liés aux produits.',
      endpoint: '/reports/stock-movements',
      fileName: 'rapport_mouvements_stock.xlsx',
      sheetName: 'Mouvements',
      typeOptions: [
        { value: 'entry', label: 'Entrée' },
        { value: 'exit', label: 'Sortie' },
      ],
      summaryCards: [
        { label: 'Mouvements', key: 'total', format: 'number' },
        { label: 'Quantité totale', key: 'sum.quantity', format: 'number' },
        { label: 'Entrées', key: 'sum.entries_quantity', format: 'number' },
        { label: 'Sorties', key: 'sum.exits_quantity', format: 'number' },
      ],
      columns: [
        { key: 'occurred_at', label: 'Date', sortable: true },
        { key: 'product_name', label: 'Produit', sortable: true },
        { key: 'product_sku', label: 'SKU', sortable: true },
        { key: 'reason_label', label: 'Motif', sortable: true },
        { key: 'type', label: 'Type', sortable: true, class: 'text-center' },
        { key: 'quantity', label: 'Qté', sortable: true, class: 'text-end' },
        { key: 'stock_before', label: 'Avant', sortable: true, class: 'text-end' },
        { key: 'stock_after', label: 'Après', sortable: true, class: 'text-end' },
        { key: 'source_type', label: 'Source', sortable: true },
        { key: 'user_name', label: 'Utilisateur', sortable: true },
      ],
    },
  },
},
{
  path: backofficePath('/rapports/transferts-stock'),
  name: 'StockTransfersReport',
  component: ModuleReportView,
  meta: {
    requiresAuth: true,
    allowedRoles: ['gerant'],
    requiresShopSelected: true,
    report: {
      title: 'Rapport des transferts de stock',
      subtitle: 'Flux envoyés et reçus entre les sites.',
      endpoint: '/reports/stock-transfers',
      fileName: 'rapport_transferts_stock.xlsx',
      sheetName: 'Transferts',
      statusOptions: [
        { value: 'draft', label: 'Brouillon' },
        { value: 'sent', label: 'Envoyé' },
        { value: 'received', label: 'Reçu' },
        { value: 'cancelled', label: 'Annulé' },
      ],
      summaryCards: [
        { label: 'Transferts', key: 'total', format: 'number' },
        { label: 'Quantité transférée', key: 'sum.total_quantity', format: 'number' },
        { label: 'Lignes produits', key: 'sum.items_count', format: 'number' },
      ],
      columns: [
        { key: 'reference', label: 'Référence', sortable: true },
        { key: 'status', label: 'Statut', sortable: true, class: 'text-center' },
        { key: 'source_shop_name', label: 'Site source', sortable: true },
        { key: 'destination_shop_name', label: 'Site destination', sortable: true },
        { key: 'items_count', label: 'Lignes', sortable: true, class: 'text-end' },
        { key: 'total_quantity', label: 'Quantité', sortable: true, class: 'text-end' },
        { key: 'sent_at', label: 'Envoi', sortable: true },
        { key: 'received_at', label: 'Réception', sortable: true },
      ],
    },
  },
},
{
  path: backofficePath('/rapports/caisses'),
  name: 'CashRegistersReport',
  component: ModuleReportView,
  meta: {
    requiresAuth: true,
    allowedRoles: ['gerant'],
    requiresShopSelected: true,
    report: {
      title: 'Rapport des sessions de caisse',
      subtitle: 'Ouvertures, clôtures, montants attendus et écarts.',
      endpoint: '/reports/cash-registers',
      fileName: 'rapport_sessions_caisse.xlsx',
      sheetName: 'Caisses',
      statusOptions: [
        { value: 'open', label: 'Ouverte' },
        { value: 'closed', label: 'Fermée' },
      ],
      summaryCards: [
        { label: 'Sessions', key: 'total', format: 'number' },
        { label: 'Montant attendu', key: 'sum.expected_amount', format: 'currency' },
        { label: 'Montant clôturé', key: 'sum.closing_amount', format: 'currency' },
        { label: 'Écart total', key: 'sum.difference', format: 'currency' },
      ],
      columns: [
        { key: 'register_name', label: 'Caisse', sortable: true },
        { key: 'cashier_name', label: 'Caissier', sortable: true },
        { key: 'status', label: 'Statut', sortable: true, class: 'text-center' },
        { key: 'opened_at', label: 'Ouverture', sortable: true },
        { key: 'closed_at', label: 'Clôture', sortable: true },
        { key: 'opening_amount', label: 'Fond initial', sortable: true, class: 'text-end' },
        { key: 'expected_amount', label: 'Théorique', sortable: true, class: 'text-end' },
        { key: 'closing_amount', label: 'Clôturé', sortable: true, class: 'text-end' },
        { key: 'difference', label: 'Écart', sortable: true, class: 'text-end' },
      ],
    },
  },
},
{
  path: backofficePath('/rapports/livraisons'),
  name: 'DeliveriesReport',
  component: ModuleReportView,
  meta: {
    requiresAuth: true,
    allowedRoles: ['gerant'],
    requiresShopSelected: true,
    requiresEcommerce: true,
    report: {
      title: 'Rapport des livraisons',
      subtitle: 'Suivi des livraisons, livreurs, zones et frais.',
      endpoint: '/reports/deliveries',
      fileName: 'rapport_livraisons.xlsx',
      sheetName: 'Livraisons',
      statusOptions: [
        { value: 'pending', label: 'En attente' },
        { value: 'assigned', label: 'Assignée' },
        { value: 'delivered', label: 'Livrée' },
        { value: 'cancelled', label: 'Annulée' },
      ],
      summaryCards: [
        { label: 'Livraisons', key: 'total', format: 'number' },
        { label: 'Frais livraison', key: 'sum.delivery_fee', format: 'currency' },
        { label: 'Ventes livrées', key: 'sum.sale_total_amount', format: 'currency' },
      ],
      columns: [
        { key: 'sale_reference', label: 'Vente', sortable: true },
        { key: 'customer_name', label: 'Client', sortable: true },
        { key: 'status', label: 'Statut', sortable: true, class: 'text-center' },
        { key: 'zone_name', label: 'Zone', sortable: true },
        { key: 'deliverer_name', label: 'Livreur', sortable: true },
        { key: 'delivery_fee', label: 'Frais', sortable: true, class: 'text-end' },
        { key: 'created_at', label: 'Création', sortable: true },
        { key: 'delivered_at', label: 'Livraison', sortable: true },
      ],
    },
  },
},
{
  path: backofficePath('/rapports/comptabilite/journal'),
  name: 'AccountingJournalReport',
  component: ModuleReportView,
  meta: {
    requiresAuth: true,
    allowedRoles: ['gerant'],
    requiresShopSelected: true,
    report: {
      title: 'Journal comptable',
      subtitle: 'Écritures comptables enregistrées pour le site courant.',
      endpoint: '/reports/accounting-journal',
      fileName: 'journal_comptable.xlsx',
      sheetName: 'Journal',
      statusOptions: [
        { value: 'posted', label: 'Comptabilisé' },
        { value: 'draft', label: 'Brouillon' },
        { value: 'cancelled', label: 'Annulé' },
      ],
      summaryCards: [
        { label: 'Écritures', key: 'total', format: 'number' },
        { label: 'Total débit', key: 'sum.total_debit', format: 'currency' },
        { label: 'Total crédit', key: 'sum.total_credit', format: 'currency' },
      ],
      columns: [
        { key: 'entry_date', label: 'Date', sortable: true },
        { key: 'entry_number', label: 'Pièce', sortable: true },
        { key: 'journal_code', label: 'Journal', sortable: true, class: 'text-center' },
        { key: 'source', label: 'Source', sortable: true },
        { key: 'description', label: 'Libellé', sortable: true },
        { key: 'status', label: 'Statut', sortable: true, class: 'text-center' },
        { key: 'total_debit', label: 'Débit', sortable: true, class: 'text-end' },
        { key: 'total_credit', label: 'Crédit', sortable: true, class: 'text-end' },
      ],
    },
  },
},
{
  path: backofficePath('/rapports/comptabilite/grand-livre'),
  name: 'AccountingLedgerReport',
  component: ModuleReportView,
  meta: {
    requiresAuth: true,
    allowedRoles: ['gerant'],
    requiresShopSelected: true,
    report: {
      title: 'Grand livre comptable',
      subtitle: 'Détail des mouvements par compte.',
      endpoint: '/reports/accounting-ledger',
      fileName: 'grand_livre_comptable.xlsx',
      sheetName: 'Grand livre',
      statusOptions: [
        { value: 'posted', label: 'Comptabilisé' },
        { value: 'draft', label: 'Brouillon' },
        { value: 'cancelled', label: 'Annulé' },
      ],
      summaryCards: [
        { label: 'Lignes', key: 'total', format: 'number' },
        { label: 'Total débit', key: 'sum.debit', format: 'currency' },
        { label: 'Total crédit', key: 'sum.credit', format: 'currency' },
      ],
      columns: [
        { key: 'account_number', label: 'Compte', sortable: true },
        { key: 'account_label', label: 'Intitulé', sortable: true },
        { key: 'entry_date', label: 'Date', sortable: true },
        { key: 'entry_number', label: 'Pièce', sortable: true },
        { key: 'description', label: 'Libellé', sortable: true },
        { key: 'debit', label: 'Débit', sortable: true, class: 'text-end' },
        { key: 'credit', label: 'Crédit', sortable: true, class: 'text-end' },
        { key: 'balance', label: 'Solde', sortable: true, class: 'text-end' },
      ],
    },
  },
},
{
  path: backofficePath('/rapports/comptabilite/balance'),
  name: 'AccountingBalanceReport',
  component: ModuleReportView,
  meta: {
    requiresAuth: true,
    allowedRoles: ['gerant'],
    requiresShopSelected: true,
    report: {
      title: 'Balance comptable',
      subtitle: 'Soldes débit/crédit par compte.',
      endpoint: '/reports/accounting-balance',
      fileName: 'balance_comptable.xlsx',
      sheetName: 'Balance',
      statusOptions: [
        { value: 'posted', label: 'Comptabilisé' },
        { value: 'draft', label: 'Brouillon' },
        { value: 'cancelled', label: 'Annulé' },
      ],
      summaryCards: [
        { label: 'Comptes', key: 'total', format: 'number' },
        { label: 'Total débit', key: 'sum.debit', format: 'currency' },
        { label: 'Total crédit', key: 'sum.credit', format: 'currency' },
        { label: 'Solde', key: 'sum.balance', format: 'currency' },
      ],
      columns: [
        { key: 'account_number', label: 'Compte', sortable: true },
        { key: 'account_label', label: 'Intitulé', sortable: true },
        { key: 'debit', label: 'Débit', sortable: true, class: 'text-end' },
        { key: 'credit', label: 'Crédit', sortable: true, class: 'text-end' },
        { key: 'balance', label: 'Solde', sortable: true, class: 'text-end' },
      ],
    },
  },
},
{
  path: backofficePath('/rapports/comptabilite/anomalies'),
  name: 'AccountingExceptionsReport',
  component: ModuleReportView,
  meta: {
    requiresAuth: true,
    allowedRoles: ['gerant'],
    requiresShopSelected: true,
    report: {
      title: 'Anomalies comptables',
      subtitle: 'Mappings manquants et erreurs de comptabilisation à traiter.',
      endpoint: '/reports/accounting-exceptions',
      fileName: 'anomalies_comptables.xlsx',
      sheetName: 'Anomalies',
      statusOptions: [
        { value: 'open', label: 'Ouverte' },
        { value: 'resolved', label: 'Résolue' },
      ],
      summaryCards: [
        { label: 'Anomalies', key: 'total', format: 'number' },
        { label: 'Ouvertes', key: 'sum.open', format: 'number' },
        { label: 'Résolues', key: 'sum.resolved', format: 'number' },
      ],
      columns: [
        { key: 'created_at', label: 'Date', sortable: true },
        { key: 'source', label: 'Source', sortable: true },
        { key: 'event', label: 'Événement', sortable: true },
        { key: 'message', label: 'Message', sortable: true },
        { key: 'status', label: 'Statut', sortable: true, class: 'text-center' },
      ],
    },
  },
},


  // Fallback
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'Sites' },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior: appScrollBehavior,
});

router.afterEach(() => {
  stopRouteLoading(store);
  applyDefaultAppSeo();
});

router.onError(() => {
  stopRouteLoading(store);
});

// Ces gardes guident la navigation selon le profil local. Les mêmes restrictions
// doivent être appliquées par l'API, qui reste accessible sans passer par Vue.
router.beforeEach(async (to, from, next) => {
  startRouteLoading(store);

  try {
    await store.dispatch('runtime/load');
  } catch (error) {
    console.warn('Impossible de charger la configuration runtime', error);
    return next(false);
  }

  const isBackofficeRoute = to.path.startsWith(BACKOFFICE_PREFIX);

  if (!isBackofficeRoute) {
    return next();
  }

  const isAuthenticated = store.getters['auth/isAuthenticated'];
  let user = store.getters['auth/currentUser'];
  const userShopId = store.getters['auth/currentUser']?.shop_id || null;

  // 1️⃣ Si la route exige une auth mais qu'on n'est pas connecté → login
  if (to.meta.requiresAuth && !isAuthenticated) {
    return next({ name: 'Login' });
  }

  // 2️⃣ Si on est connecté mais que user n'est pas encore chargé (ex: après F5) → le recharger
  if (isAuthenticated && !user) {
    try {
      await store.dispatch('auth/LOAD_USER_FROM_LOCALSTORAGE');
    } catch (e) {
      console.warn('Erreur LOAD_USER_FROM_LOCALSTORAGE', e);
    }
    user = store.getters['auth/currentUser'];
  }

  if (isAuthenticated && user?.must_change_password && to.name !== 'ChangePassword') {
    return next({ name: 'ChangePassword' });
  }

  const role = user?.role;
  const requiredPermissions = routePermissions(to);
  const anyPermissions = to.meta.permissionsAny || [];
  const hasRoutePermission = hasAllPermissions(user, requiredPermissions) && (!anyPermissions.length || hasAnyPermission(user, anyPermissions));
  const disabledRequiredModule = [...requiredPermissions, ...anyPermissions]
    .map((permission) => permission.split('.', 1)[0])
    .find((module) => module && !store.getters['runtime/moduleEnabled'](module));
  if (disabledRequiredModule) {
    const landingRoute = authenticatedLandingRoute(user, store.getters['runtime/moduleEnabled']);
    return landingRoute === to.name ? next() : next({ name: landingRoute });
  }
  // Interdiction d’aller ailleurs sans shop sélectionné (sauf pages autorisées)
  const requiresShopSelected = to.meta.requiresShopSelected !== false; // true par défaut
  if (requiresShopSelected) {
    // Un compte multisite sans contexte courant doit d'abord choisir une boutique.
    if (['gerant', 'utilisateur'].includes(String(role)) && !userShopId && !store.getters['shop/currentShopId']) {
      return next({ name: 'Sites' });
    }
  }

  // 3️⃣ Si on est déjà connecté et qu'on va sur /backoffice/login → rediriger selon le rôle
  if (to.meta.redirectIfAuth && isAuthenticated) {
    if (role === 'caissier') {
      return next({ name: 'CashierSales' });
    }
    return next({ name: 'Dashboard' });
  }

  // 4️⃣ REDIRECTION AUTO POUR LE CAISSIER QUI VA SUR "Dashboard"
  if (to.name === 'Dashboard' && role === 'caissier') {
    return next({ name: 'CashierSales' });
  }

  // 5️⃣ RESTRICTION D’ACCÈS SELON allowedRoles
  const allowedRoles = to.meta.allowedRoles;

  if (allowedRoles && role && !allowedRoles.includes(role) && !hasRoutePermission) {
    // caissier qui essaie une page gérant → caisse
    if (role === 'caissier') {
      return next({ name: 'CashierSales' });
    }

    // autre rôle non autorisé → première page accessible si connecté
    if (isAuthenticated) {
      const landingRoute = authenticatedLandingRoute(user, store.getters['runtime/moduleEnabled']);
      return landingRoute === to.name ? next() : next({ name: landingRoute });
    }
    return next({ name: 'Login' });
  }

  if (to.name === 'Sites') {
    const isGlobalUser = role === 'utilisateur' && !userShopId;
    const canViewSites = role === 'gerant'
      || (isGlobalUser && hasPermission(user, 'shops.view'))
      || hasPermission(user, 'settings.view');

    if (!canViewSites) {
      return next({ name: role === 'caissier' ? 'CashierSales' : 'Dashboard' });
    }
    if (role === 'gerant' && userShopId) {
      return next({ name: 'Dashboard' });
    }
    return next();
  }

  if (!hasRoutePermission) {
    if (role === 'caissier') {
      return next({ name: 'CashierSales' });
    }

    const landingRoute = authenticatedLandingRoute(user, store.getters['runtime/moduleEnabled']);
    return landingRoute === to.name ? next() : next({ name: landingRoute });
  }

  // 6️⃣ Vérif infos boutique complètes
  // 👉 On NE fait cette vérif que si :
  // - on est authentifié
  // - on est gérant
  // - on n'est PAS déjà sur la page ShopInfo
  // - et on a déjà un shop sélectionné (currentShopId)
  if (
    isAuthenticated &&
    role === 'gerant' &&
    to.name !== 'ShopInfo'
  ) {
    const currentShopId = store.getters['shop/currentShopId'];

    // 🛑 Si aucun shop courant n'est encore sélectionné,
    // on NE force pas la redirection, on laisse charger l'app.
    if (!currentShopId) {
      return next();
    }

    try {
      await ensureShopInfoForNavigation(store);
    } catch (e) {
      console.warn('Erreur shop/fetchInfo', e);
    }

    // ✅ On ne redirige vers la configuration que si on sait
    // que le shop courant est réellement incomplet
    if (isCurrentShopKnownIncomplete(store.getters)) {
      return next({ name: 'ShopInfo' });
    }
  }

  if (to.meta.requiresPublishedMainEcommerce) {
    try {
      await ensureShopInfoForNavigation(store);
    } catch (e) {
      console.warn('Erreur shop/fetchInfo pour la garde e-commerce', e);
    }

    const shop = store.getters['shop/shopInfo'];
    if (shop?.is_main !== true || shop?.ecommerce_enabled !== true) {
      // The site detail is the only place where publication can now be enabled safely.
      return next({ name: 'SiteShow', params: { id: store.getters['shop/currentShopId'] } });
    }
  }

  return next();
});



export default router;
